import { useState, useEffect, useRef } from 'react';
import { Linkedin, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useLocation } from 'react-router-dom';
import { scrollToHash } from '@/lib/scroll';
import { useSectionNavigation } from '@/hooks/useSectionNavigation';

const navLinks = [
  { href: '#solutions', label: 'Solutions' },
  { href: '#services', label: 'Services' },
  { href: '#why', label: 'Why Yaw' },
  { href: '#faq', label: 'FAQ' },
  { href: '#contact', label: 'Contact' },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isContactPage = location.pathname === '/contact';
  const pendingHashRef = useRef<string | null>(null);
  const goToSection = useSectionNavigation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock background scroll while the mobile menu is open. `overflow: hidden`
  // alone doesn't hold on iOS, so we pin the body in place. On close we restore
  // the scroll position and, if a nav link was tapped, scroll to that section.
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const scrollY = window.scrollY;
    const { body } = document;
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, scrollY);

      const pending = pendingHashRef.current;
      if (pending) {
        pendingHashRef.current = null;
        requestAnimationFrame(() => scrollToHash(pending));
      }
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      if (location.pathname === '/') {
        // Home: defer the scroll until the menu closes and the lock releases.
        pendingHashRef.current = href;
      } else {
        // Off the home page: route home with the hash; ScrollManager scrolls.
        goToSection(href);
      }
    } else {
      goToSection(href);
    }
  };

  return (
    <>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Scroll Progress Bar (CSS transform, no JS animation lib) */}
      <div
        className="fixed top-0 left-0 right-0 h-0.5 bg-orange z-[60] origin-left transition-transform duration-300"
        style={{ transform: isScrolled ? 'scaleX(1)' : 'scaleX(0)' }}
        aria-hidden="true"
      />

      <header
        className={`header-in fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-navy-deep/95 backdrop-blur-md shadow-lg shadow-black/20'
            : 'bg-transparent'
        }`}
      >
        <nav className="container mx-auto px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between">
          <a
            href="#"
            className="focus-ring rounded"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img
              src="/logo-transparent-240.webp"
              alt="Yaw CTV"
              width={240}
              height={170}
              fetchPriority="high"
              decoding="async"
              className="h-12 sm:h-14 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 lg:gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-white/70 hover:text-white transition-colors duration-200 text-sm font-medium focus-ring rounded px-2 py-1"
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://www.linkedin.com/company/yawdigital"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit our LinkedIn page"
              className="text-white/70 hover:text-orange transition-colors duration-200 focus-ring rounded p-1"
            >
              <Linkedin size={20} />
            </a>
            {!isContactPage && (
              <Button
                asChild
                className="bg-orange hover:bg-orange-hover text-white font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange/20"
              >
                <Link to="/contact">
                  Get Started
                </Link>
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2 focus-ring rounded"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Mobile Menu Overlay (CSS fade-in; closes instantly) */}
      {isMobileMenuOpen && (
        <div
          className="menu-fade-in fixed inset-0 z-40 bg-[hsl(200,55%,18%)] lg:hidden"
          style={{ touchAction: 'none', overscrollBehavior: 'none' }}
        >
          <div className="flex flex-col items-center justify-center h-full gap-3 sm:gap-4">
            {navLinks.map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                style={{ animationDelay: `${index * 0.05}s` }}
                className="menu-item-in text-lg sm:text-xl font-medium text-white hover:text-orange transition-colors focus-ring rounded px-4 py-1"
              >
                {link.label}
              </a>
            ))}

            <div
              className="menu-item-in flex gap-3 mt-3 sm:mt-4"
              style={{ animationDelay: '0.2s' }}
            >
              <span className="px-3 py-1 bg-teal/20 text-teal-light text-xs font-medium rounded-full">
                SDVOSB
              </span>
              <span className="px-3 py-1 bg-teal/20 text-teal-light text-xs font-medium rounded-full">
                MBO
              </span>
            </div>

            {!isContactPage && (
              <div
                className="menu-item-in mt-2"
                style={{ animationDelay: '0.25s' }}
              >
                <Button
                  asChild
                  className="bg-orange hover:bg-orange-hover text-white font-semibold px-6 py-2 text-sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Link to="/contact">
                    Get Started
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
